import { lazy } from "react";

import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";

import AdminRoute from "./components/AdminRoute";
import { AuthCallback } from "./components/AuthCallback";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import ReportAssistantCaseDetail from "./components/ReportAssistantCaseDetail";
import ReportAssistantHistory from "./components/ReportAssistantHistory";
import { RoleLanding } from "./components/RoleLanding";
import { SigningOut } from "./components/SigningOut";
import AccessConfigurationPage from "./pages/AccessConfiguration";
import AccessControlPage from "./pages/AccessControl";
import AuditLogPage from "./pages/AuditLog";
import BookingApprovalsPage from "./pages/BookingApprovals";
import CasesPage from "./pages/Cases";
import CaseSummaryPage from "./pages/Cases/CaseSummary";
import InProgress from "./pages/InProgress";
import Landing from "./pages/Landing";
import NewCasePage from "./pages/NewCase";
import NotFound from "./pages/NotFound";
import ProviderRequestsPage from "./pages/ProviderPortal/Requests";
import ProviderRequestDetailPage from "./pages/ProviderPortal/Requests/RequestDetail";
import ReportStudioPage from "./pages/ReportStudio";
import AudioTranscriberPage from "./pages/ReportStudio/AudioTranscriber";
import AudioTranscriberResultJsonPage from "./pages/ReportStudio/AudioTranscriber/ResultJson";
import ReportStudioCaseDetail from "./pages/ReportStudio/CaseDetail";
import DocumentSorterPage from "./pages/ReportStudio/DocumentSorter";
import DocumentSorterResultJsonPage from "./pages/ReportStudio/DocumentSorter/ResultJson";
import { ManualClassificationPreview } from "./pages/ReportStudio/DocumentSorter/components/BundleUpload/Processes/ManualClassification/__mocks__/ManualClassificationPreview";
import { BundleUploadStepPreview } from "./pages/ReportStudio/DocumentSorter/components/BundleUpload/__mocks__/BundleUploadStepPreview";
import ReportBuilderPage from "./pages/ReportStudio/ReportBuilder";
import ReportBuilderResultJsonPage from "./pages/ReportStudio/ReportBuilder/ResultJson";
import RequestDocumentsPage from "./pages/ReportStudio/RequestDocuments";
import ReportTemplatesPage from "./pages/ReportTemplates";
import UsersAdminPage from "./pages/UsersAdmin";

const LayoutWithProtection = () => (
  <ProtectedRoute>
    <RoleLanding />
    <Layout />
  </ProtectedRoute>
);

// Root route
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});

// Public routes
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Landing,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth-callback",
  component: AuthCallback,
});

const signingOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signing-out",
  component: SigningOut,
});

// Protected layout route (pathless)
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "layout",
  component: LayoutWithProtection,
});

// Top-level protected routes
const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/dashboard",
  component: InProgress,
});

const newCaseRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/new-case",
  component: NewCasePage,
});

const casesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/cases",
  component: CasesPage,
});

const caseSummaryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/cases/$caseNumber",
  component: CaseSummaryPage,
});

// Provider portal (MDRS-157) — Requests list + detail; Dashboard/Account stubbed.
const providerDashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/provider/dashboard",
  component: InProgress,
});

const providerRequestsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/provider/requests",
  component: ProviderRequestsPage,
});

// "Log request" wizard is deferred (Requests-first) — static route wins over the
// `$requestId` param route below, so it renders the stub, not the detail page.
const providerNewRequestRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/provider/requests/new",
  component: InProgress,
});

const providerRequestDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/provider/requests/$requestId",
  component: ProviderRequestDetailPage,
});

const providerAccountRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/provider/account",
  component: InProgress,
});

const reportStudioRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/report-studio",
  component: ReportStudioPage,
});

// Report Studio case detail (tab layout)
const reportStudioCaseRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/report-studio/$id",
  component: ReportStudioCaseDetail,
});

// Case Overview (index route)
const reportStudioCaseIndexRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "/",
  component: lazy(() =>
    import("./pages/ReportStudio/CaseOverview").then((m) => ({
      default: m.CaseOverview,
    }))
  ),
});

// Assessment Prep tab
const edgePrepareTabRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "assessment-prep",
  component: DocumentSorterPage,
});

const edgePrepareResultJsonRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "assessment-prep/result.json",
  component: DocumentSorterResultJsonPage,
});

// Request Documents tab (MDRS-157) — referrer requests documents from a provider
const requestDocumentsTabRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "request-documents",
  component: RequestDocumentsPage,
});

// Assessment Scribe tab
//
// `section` powers the SCRIBE-07 back-link: a mapped Report Builder note card
// navigates here with the AssessmentSection id to land on. Deliberately does
// NOT carry the note's extract text (PHI/clinical content) — that would sit
// in the URL/browser history for the tab's lifetime. The text travels via an
// in-memory handoff instead (see AudioTranscriber/utils/pendingHighlightHandoff.ts),
// consumed once and cleared on read. Optional so a normal tab click (no
// search params) behaves exactly as before.
export type AssessmentScribeSearch = {
  section?: string;
};

const edgeCaptureTabRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "assessment-scribe",
  component: AudioTranscriberPage,
  validateSearch: (search: Record<string, unknown>): AssessmentScribeSearch => ({
    section: typeof search.section === "string" ? search.section : undefined,
  }),
});

const edgeCaptureResultJsonRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "assessment-scribe/result.json",
  component: AudioTranscriberResultJsonPage,
});

// Report Builder tab
const edgeComposeTabRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "report-builder",
  component: ReportAssistantCaseDetail,
});

const edgeComposeTemplateRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "report-builder/template/$templateId",
  component: ReportBuilderPage,
});

const edgeComposeResultJsonRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "report-builder/template/$templateId/result.json",
  component: ReportBuilderResultJsonPage,
});

const edgeComposeHistoryRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "report-builder/history",
  component: ReportAssistantHistory,
});

// Report QA tab
const edgeElevateTabRoute = createRoute({
  getParentRoute: () => reportStudioCaseRoute,
  path: "report-qa",
  component: InProgress,
});

// Report Templates
const reportTemplatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/report-templates",
  component: ReportTemplatesPage,
});

// Other nav routes
const resourcesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/resources",
  component: InProgress,
});

const invoiceStudioRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/invoice-studio",
  component: InProgress,
});

const userManagementRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/user-management",
  component: InProgress,
});

const accessLogsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/access-logs",
  component: InProgress,
});

// Internal admin: Report Builder access control (approval list). The page
// itself also guards on is_admin.
const accessControlRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/access-control",
  component: AccessControlPage,
});

// Internal admin: all users (list + edit user type / flags). Page guards on is_admin.
const usersAdminRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/users-admin",
  component: UsersAdminPage,
});

// Internal admin: dynamic access configuration per user type (MDRS-162).
// Gated by AdminRoute (consistent with Booking Approvals); the page + its
// queries also self-guard, and every endpoint enforces require_admin.
const accessConfigRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/access-config",
  component: () => (
    <AdminRoute>
      <AccessConfigurationPage />
    </AdminRoute>
  ),
});

// Admin-only Booking Approvals (MDRS-102)
const bookingApprovalsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/booking-approvals",
  component: () => (
    <AdminRoute>
      <BookingApprovalsPage />
    </AdminRoute>
  ),
});

// Internal admin: report audit log (MDRS-271, widened by MDRS-455). Page +
// endpoint are both admin-gated; wrapped in AdminRoute for consistency.
const auditLogRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/audit-log",
  component: () => (
    <AdminRoute>
      <AuditLogPage />
    </AdminRoute>
  ),
});

// Dev-only preview routes (excluded from production builds)
const isDev = import.meta.env.DEV;

const devBundleUploadPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/__dev/bundle-upload",
  component: BundleUploadStepPreview,
});

const devManualClassificationPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/__dev/manual-classification",
  component: ManualClassificationPreview,
});

const devRoutes = isDev
  ? [devBundleUploadPreviewRoute, devManualClassificationPreviewRoute]
  : [];

// Build route tree
const routeTree = rootRoute.addChildren([
  landingRoute,
  authCallbackRoute,
  signingOutRoute,
  ...devRoutes,
  layoutRoute.addChildren([
    dashboardRoute,
    newCaseRoute,
    casesRoute,
    caseSummaryRoute,
    providerDashboardRoute,
    providerRequestsRoute,
    providerNewRequestRoute,
    providerRequestDetailRoute,
    providerAccountRoute,
    reportStudioRoute,
    reportTemplatesRoute,
    reportStudioCaseRoute.addChildren([
      reportStudioCaseIndexRoute,
      edgePrepareTabRoute,
      edgePrepareResultJsonRoute,
      requestDocumentsTabRoute,
      edgeCaptureTabRoute,
      edgeCaptureResultJsonRoute,
      edgeComposeTabRoute,
      edgeComposeTemplateRoute,
      edgeComposeResultJsonRoute,
      edgeComposeHistoryRoute,
      edgeElevateTabRoute,
    ]),
    resourcesRoute,
    invoiceStudioRoute,
    userManagementRoute,
    accessLogsRoute,
    accessControlRoute,
    usersAdminRoute,
    accessConfigRoute,
    bookingApprovalsRoute,
    auditLogRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
